import errors from './errors.js'
import { getRecipeVocabularyValues } from './recipeVocabularies.js'
const { ValidationError } = errors

// ============================================
// CONSTANTS
// ============================================
const LIMITS = {
    USER_ID_MIN: 1,
    NAME_MIN: 2,
    USERNAME_MIN: 5,
    USERNAME_MAX: 25,
    PASSWORD_MIN: 8,
    EMAIL_MAX: 254,
    PHONE_MIN: 8,
    PHONE_MAX: 20,
    URL_MAX: 254,
    DESCRIPTION_MAX: 255,
    BIO_MAX: 1000,
    NOTES_MAX: 255,
    PACK_NAME_MIN: 5,
    PACK_NAME_MAX: 50,
    CURRENCY_LENGTH: 3,
    DNI_MAX: 9,
    PAYMENT_MAX_LENGTH: 10,
    EXPIRING_TIME_MIN: 1,
    EXPIRING_TIME_MAX: 12,
    // Address limits
    ADDRESS_FULL_NAME_MIN: 2,
    ADDRESS_FULL_NAME_MAX: 100,
    ADDRESS_LINE1_MIN: 5,
    ADDRESS_LINE1_MAX: 200,
    ADDRESS_LINE2_MAX: 100,
    ADDRESS_CITY_MIN: 2,
    ADDRESS_CITY_MAX: 100,
    ADDRESS_STATE_MAX: 100,
    ADDRESS_POSTAL_CODE_MIN: 3,
    ADDRESS_POSTAL_CODE_MAX: 20,
    ADDRESS_COUNTRY_LENGTH: 2,
    /** Max length for public recipe image URLs (e.g. Supabase Storage) on CSV import apply */
    RECIPE_IMPORT_IMAGE_URL_MAX: 2048,
    /** DB column `allergens.name` is varchar(50) */
    ALLERGEN_NAME_MAX: 50,
    /** Mirrors `ck_recipe_feedback_comment_length` on `recipe_feedback_events` */
    RECIPE_FEEDBACK_COMMENT_MAX: 500
}

const REGEX = {
    EMAIL: /^(([^<>()[\]\.,;:\s@\"]+(\.[^<>()[\]\.,;:\s@\"]+)*)|(\".+\"))@(([^<>()[\]\.,;:\s@\"]+\.)+[^<>()[\]\.,;:\s@\"]{2,})$/i,
    PHONE: /^\+?[\d\s\-()]{8,20}$/,
    URL: /^(https?:\/\/)?(www\.)?([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,6}(\/[^\s]*)?$/i,
    DNI_SPAIN: /^[0-9]{8}[A-Z]$/i,
    TIME_FORMAT: /^[-+]?([0-9]{2}):([0-5][0-9]):([0-5][0-9])$/i,
    UUID: /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
}

const VALID_VALUES = {
    UNITS: ['hours', 'units'],
    STATUS: ['Pending', 'Active', 'Expired', 'Finished'],
    PAYMENT_METHODS: ['card', 'bankTransfer', 'paypal', 'stripe', 'cash', 'others'],
    TASK_STATUS: ['Pending', 'In Progress', 'On Hold', 'Completed', 'Cancelled'],
    TASK_PRIORITY: ['Low', 'Medium', 'High', 'Urgent'],
    DIETS: getRecipeVocabularyValues('dietTypes'),

    // User profile (onboarding step 1 + household rhythm).
    // Single source of truth for backend validation, DB CHECK constraints and
    // the wizard option lists — keep in sync with migration
    // 20260722120000_user_profiles_and_recipe_feedback.sql.
    PROFILE_AGE_RANGES: ['18_24', '25_34', '35_44', '45_54', '55_64', '65_plus'],
    PROFILE_GENDERS: ['female', 'male', 'non_binary', 'prefer_not_to_say'],
    PROFILE_OCCUPATION_CONTEXTS: [
        'works_outside', 'works_from_home', 'studying', 'caregiving', 'retired', 'other'
    ],
    PROFILE_PRIMARY_GOALS: [
        'save_money', 'eat_healthier', 'save_time', 'stop_deciding', 'reduce_waste', 'lose_weight'
    ],
    PROFILE_COOKING_FREQUENCIES: ['1_2_days', '3_4_days', '5_7_days'],
    PROFILE_COOKING_TIME_PREFERENCES: ['quick', 'standard', 'enjoys_cooking'],

    // Recipe feedback capture
    RECIPE_FEEDBACK_ACTIONS: ['refresh', 'delete'],
    RECIPE_FEEDBACK_REASONS: [
        'too_slow', 'other_ingredients', 'disliked', 'too_expensive', 'recently_eaten', 'other'
    ]
}

// ============================================
// HELPER FUNCTIONS
// ============================================
const isString = (value) => typeof value === 'string'
const isEmpty = (value) => value === ''
const isInRange = (value, min, max) => value >= min && value <= max

// ============================================
// BASE VALIDATORS (Reusable building blocks)
// ============================================
const validateString = (value, fieldName) => {
    if (!isString(value)) {
        throw new ValidationError(`${fieldName} must be a string`)
    }
    if (isEmpty(value)) {
        throw new ValidationError(`${fieldName} is required`)
    }
    return value.trim()
}

const validateStringLength = (value, fieldName, min = 0, max = Infinity) => {
    const trimmed = validateString(value, fieldName)
    if (!isInRange(trimmed.length, min, max)) {
        if (min && max !== Infinity) {
            throw new ValidationError(`${fieldName} must be between ${min} and ${max} characters`)
        } else if (min) {
            throw new ValidationError(`${fieldName} must be at least ${min} characters`)
        } else {
            throw new ValidationError(`${fieldName} must be at most ${max} characters`)
        }
    }
    return trimmed
}

const validateEnum = (value, fieldName, validValues) => {
    const trimmed = validateString(value, fieldName)
    if (!validValues.includes(trimmed)) {
        throw new ValidationError(`${fieldName} must be one of: ${validValues.join(', ')}`)
    }
    return trimmed
}

const validatePattern = (value, fieldName, pattern, errorMessage) => {
    const trimmed = validateString(value, fieldName)
    if (!pattern.test(trimmed)) {
        throw new ValidationError(errorMessage || `Invalid ${fieldName} format`)
    }
    return trimmed
}

// ============================================
// SPECIFIC VALIDATORS
// ============================================

const validateUserId = userId => {
    if (!userId || typeof userId !== 'number' || userId < LIMITS.USER_ID_MIN) {
        throw new ValidationError('Invalid user ID provided')
    }
}

const validateName = name => validateStringLength(name, 'Name', LIMITS.NAME_MIN)

const validatePackName = name => validateStringLength(name, 'Pack name', LIMITS.PACK_NAME_MIN, LIMITS.PACK_NAME_MAX)

const validateDescription = description => validateStringLength(description, 'Description', 0, LIMITS.DESCRIPTION_MAX)

const validateBio = bio => validateStringLength(bio, 'Biography', 0, LIMITS.BIO_MAX)

const validateDate = date => {
    if (!(date instanceof Date) || isNaN(date.getTime())) {
        throw new ValidationError('Invalid date: must be a valid Date object')
    }
    return date
}

const validateInteger = num => {
    if (!Number.isInteger(num)) {
        throw new ValidationError('Value must be an integer')
    }
    return num
}

const validateNumber = num => {
    if (typeof num !== 'number' || !Number.isFinite(num)) {
        throw new ValidationError('Value must be a valid number')
    }
    return num
}

const validateEmail = email => {
    validateStringLength(email, 'Email', 1, LIMITS.EMAIL_MAX)
    return validatePattern(email, 'Email', REGEX.EMAIL, 'Invalid email format').toLowerCase()
}

const validatePhone = phone => validatePattern(phone, 'Phone', REGEX.PHONE, 'Invalid phone number format')

const validateUsername = username => validateStringLength(username, 'Username', LIMITS.USERNAME_MIN, LIMITS.USERNAME_MAX)

const validateUsernameOrEmail = input => {
    const trimmed = validateString(input, 'Username or Email')
    if (REGEX.EMAIL.test(trimmed)) {
        return validateEmail(trimmed)
    }
    return validateUsername(trimmed)
}

const validateUrl = url => validatePattern(url, 'URL', REGEX.URL, 'Invalid URL format')

/**
 * Validates image_url from CSV import when the cell is non-empty (https-only, URL parse).
 * Empty/whitespace after trim is a no-op (caller should only invoke when a value is present).
 */
const validateHttpsRecipeImportImageUrl = (rawUrl) => {
    const trimmed = String(rawUrl ?? '').trim()
    if (!trimmed) return
    if (trimmed.length > LIMITS.RECIPE_IMPORT_IMAGE_URL_MAX) {
        throw new ValidationError('image_url exceeds maximum length')
    }
    if (!trimmed.startsWith('https://')) {
        throw new ValidationError('image_url must be an https URL')
    }
    try {
        const parsed = new URL(trimmed)
        if (parsed.protocol !== 'https:') {
            throw new ValidationError('image_url must use https')
        }
    } catch (error) {
        if (error instanceof ValidationError) throw error
        throw new ValidationError('image_url is not a valid URL')
    }
}

const validatePassword = password => validateStringLength(password, 'Password', LIMITS.PASSWORD_MIN)

const validateCurrency = currency => validateStringLength(currency, 'Currency', LIMITS.CURRENCY_LENGTH, LIMITS.CURRENCY_LENGTH)

const validateMethod = method => validateString(method, 'Method')

const validatePasswordsMatch = (password, passwordRepeat) => {
    if (!isString(passwordRepeat)) throw new ValidationError('Invalid password confirmation')
    if (password !== passwordRepeat) throw new ValidationError('Passwords do not match')
}

const validateImage = image => validateString(image, 'Image')

const validateText = (text, fieldName = 'text') => validateString(text, fieldName)

const validateGenericField = (text, maxLength, fieldName = 'text') =>
    validateStringLength(text, fieldName, 0, maxLength)

const validateUnit = unit => validateEnum(unit, 'Unit', VALID_VALUES.UNITS)

const validateQuantity = quantity => validateString(quantity, 'Quantity')

const validateAllergenName = (raw) => {
    const name = validateString(raw, 'Allergen name')
    validateStringLength(name, 'Allergen name', 1, LIMITS.ALLERGEN_NAME_MAX)
    return name
}

const validatePayedAmount = amount => validateStringLength(amount, 'Payment amount', 1, LIMITS.PAYMENT_MAX_LENGTH)

const validatePromoAmount = amount => validateStringLength(amount, 'Promo amount', 1, LIMITS.PAYMENT_MAX_LENGTH)

const validateId = (id, fieldName = 'ID') => {
    const trimmed = validateString(id, fieldName)
    if (trimmed.length !== 24) {
        throw new ValidationError(`${fieldName} must be 24 characters`)
    }
    return trimmed
}

const validateUuid = (uuid, fieldName = 'UUID') => {
    const trimmed = validateString(uuid, fieldName)
    if (!REGEX.UUID.test(trimmed)) {
        throw new ValidationError(`${fieldName} must be a valid UUID format`)
    }
    return trimmed.toLowerCase()
}

const validateCallback = callback => {
    if (typeof callback !== 'function') {
        throw new ValidationError('Callback must be a function')
    }
}

const validateDni = dni => validatePattern(dni, 'DNI', REGEX.DNI_SPAIN, 'Invalid Spanish DNI format')

const validateExpiringTime = months => {
    const num = Number(months)
    if (num === 0) {
        throw new ValidationError('Expiring time is required')
    }
    if (!isInRange(num, LIMITS.EXPIRING_TIME_MIN, LIMITS.EXPIRING_TIME_MAX) && num !== -1) {
        throw new ValidationError(`Expiring time must be between ${LIMITS.EXPIRING_TIME_MIN} and ${LIMITS.EXPIRING_TIME_MAX} months`)
    }
    return num
}

const validateStatus = status => validateEnum(status, 'Status', VALID_VALUES.STATUS)

const validatePaymentMethod = method => validateEnum(method, 'Payment method', VALID_VALUES.PAYMENT_METHODS)

const validateTimeFormat = time => validatePattern(time, 'Time', REGEX.TIME_FORMAT, 'Invalid time format (+/-)hh:mm:ss')

const validateTaskStatus = status => validateEnum(status, 'Task status', VALID_VALUES.TASK_STATUS)

const validateTaskPriority = priority => validateEnum(priority, 'Task priority', VALID_VALUES.TASK_PRIORITY)

const validateNotes = notes => validateStringLength(notes, 'Notes', 0, LIMITS.NOTES_MAX)

const validateAttendees = attendees => {
    if (!Array.isArray(attendees)) {
        throw new ValidationError('Attendees must be an array')
    }
    return attendees
}

const validateArray = (value, fieldName = 'Value') => {
    if (!Array.isArray(value)) {
        throw new ValidationError(`${fieldName} must be an array`, {
            provided: typeof value,
            expected: 'array'
        })
    }
    return value
}

const validatePositiveId = (id, fieldName = 'ID') => {
    let numId = null

    if (typeof id === 'number') {
        numId = id
    } else if (typeof id === 'string') {
        const trimmed = id.trim()
        if (!/^\d+$/.test(trimmed)) {
            throw new ValidationError(`Invalid ${fieldName}`, {
                provided: id,
                providedType: typeof id,
                expected: 'positive integer'
            })
        }
        numId = Number(trimmed)
    } else {
        throw new ValidationError(`Invalid ${fieldName}`, {
            provided: id,
            providedType: typeof id,
            expected: 'positive integer'
        })
    }

    if (!Number.isInteger(numId) || numId < 1 || isNaN(numId)) {
        throw new ValidationError(`Invalid ${fieldName}`, {
            provided: id,
            providedType: typeof id,
            expected: 'positive integer'
        })
    }

    return numId
}

const validateDiet = (diet) => {
    const trimmed = validateString(diet, 'Diet')
    const normalized = trimmed.toLowerCase().trim()

    if (!VALID_VALUES.DIETS.includes(normalized)) {
        throw new ValidationError('Invalid diet type', {
            provided: diet,
            validDiets: VALID_VALUES.DIETS
        })
    }

    return normalized
}

const validateDietsArray = (diets, fieldName = 'Diets') => {
    if (!Array.isArray(diets)) {
        throw new ValidationError(`${fieldName} must be an array`)
    }

    if (diets.length === 0) {
        return []
    }

    const validatedDiets = diets.map((diet) => validateDiet(diet))
    const uniqueDiets = [...new Set(validatedDiets)]

    return uniqueDiets
}

// ============================================
// USER PROFILE VALIDATORS
// ============================================

const validateAgeRange = (ageRange) =>
    validateEnum(ageRange, 'Age range', VALID_VALUES.PROFILE_AGE_RANGES)

const validateGender = (gender) =>
    validateEnum(gender, 'Gender', VALID_VALUES.PROFILE_GENDERS)

const validateOccupationContext = (context) =>
    validateEnum(context, 'Occupation context', VALID_VALUES.PROFILE_OCCUPATION_CONTEXTS)

const validatePrimaryGoal = (goal) =>
    validateEnum(goal, 'Primary goal', VALID_VALUES.PROFILE_PRIMARY_GOALS)

const validateCookingFrequency = (frequency) =>
    validateEnum(frequency, 'Cooking frequency', VALID_VALUES.PROFILE_COOKING_FREQUENCIES)

const validateCookingTimePreference = (preference) =>
    validateEnum(preference, 'Cooking time preference', VALID_VALUES.PROFILE_COOKING_TIME_PREFERENCES)

// ============================================
// RECIPE FEEDBACK VALIDATORS
// ============================================

const validateRecipeFeedbackAction = (action) =>
    validateEnum(action, 'Recipe feedback action', VALID_VALUES.RECIPE_FEEDBACK_ACTIONS)

const validateRecipeFeedbackReason = (reason) =>
    validateEnum(reason, 'Recipe feedback reason', VALID_VALUES.RECIPE_FEEDBACK_REASONS)

/**
 * Free-text comment attached to a recipe refresh/delete. Optional by design:
 * blank input means "the user chose not to elaborate", not an error.
 * Returns null when there is nothing worth storing.
 */
const validateRecipeFeedbackComment = (comment) => {
    if (comment === undefined || comment === null) {
        return null
    }

    if (!isString(comment)) {
        throw new ValidationError('Recipe feedback comment must be a string')
    }

    const trimmed = comment.trim()
    if (trimmed === '') {
        return null
    }

    if (trimmed.length > LIMITS.RECIPE_FEEDBACK_COMMENT_MAX) {
        throw new ValidationError(
            `Recipe feedback comment must be at most ${LIMITS.RECIPE_FEEDBACK_COMMENT_MAX} characters`
        )
    }

    return trimmed
}

// ============================================
// ADDRESS VALIDATORS
// ============================================

const validateFullName = (fullName) => {
    return validateStringLength(
        fullName,
        'Full name',
        LIMITS.ADDRESS_FULL_NAME_MIN,
        LIMITS.ADDRESS_FULL_NAME_MAX
    )
}

const validateAddressLine = (line, fieldName = 'Address line', minLength = LIMITS.ADDRESS_LINE1_MIN, maxLength = LIMITS.ADDRESS_LINE1_MAX) => {
    return validateStringLength(line, fieldName, minLength, maxLength)
}

const validateCity = (city) => {
    return validateStringLength(
        city,
        'City',
        LIMITS.ADDRESS_CITY_MIN,
        LIMITS.ADDRESS_CITY_MAX
    )
}

const validatePostalCode = (postalCode) => {
    return validateStringLength(
        postalCode,
        'Postal code',
        LIMITS.ADDRESS_POSTAL_CODE_MIN,
        LIMITS.ADDRESS_POSTAL_CODE_MAX
    )
}

const validateCountryCode = (code) => {
    const trimmed = validateString(code, 'Country code')
    if (trimmed.length !== LIMITS.ADDRESS_COUNTRY_LENGTH) {
        throw new ValidationError(`Country code must be exactly ${LIMITS.ADDRESS_COUNTRY_LENGTH} characters`)
    }
    if (!/^[A-Z]{2}$/.test(trimmed)) {
        throw new ValidationError('Country code must be 2 uppercase letters (ISO 3166-1 alpha-2)')
    }
    return trimmed
}

const validateAddressFormat = (address) => {
    const errors = []

    try {
        validateFullName(address.fullName)
    } catch (error) {
        errors.push(error.message)
    }

    try {
        validateAddressLine(address.line1, 'Street address')
    } catch (error) {
        errors.push(error.message)
    }

    if (address.line2) {
        try {
            validateStringLength(address.line2, 'Address line 2', 0, LIMITS.ADDRESS_LINE2_MAX)
        } catch (error) {
            errors.push(error.message)
        }
    }

    try {
        validateCity(address.city)
    } catch (error) {
        errors.push(error.message)
    }

    if (address.state) {
        try {
            validateStringLength(address.state, 'State', 0, LIMITS.ADDRESS_STATE_MAX)
        } catch (error) {
            errors.push(error.message)
        }
    }

    // Postal code is optional (some addresses from Google Places don't have postal codes)
    if (address.postalCode) {
        try {
            validatePostalCode(address.postalCode)
        } catch (error) {
            errors.push(error.message)
        }
    }

    try {
        validateCountryCode(address.country || 'ES')
    } catch (error) {
        errors.push(error.message)
    }

    if (address.phone) {
        try {
            validatePhone(address.phone)
        } catch (error) {
            errors.push(error.message)
        }
    }

    if (errors.length > 0) {
        throw new ValidationError('Address validation failed', { errors })
    }

    return {
        fullName: validateFullName(address.fullName),
        phone: address.phone ? validatePhone(address.phone) : undefined,
        line1: validateAddressLine(address.line1, 'Street address'),
        line2: address.line2 ? validateStringLength(address.line2, 'Address line 2', 0, LIMITS.ADDRESS_LINE2_MAX) : undefined,
        city: validateCity(address.city),
        state: address.state ? validateStringLength(address.state, 'State', 0, LIMITS.ADDRESS_STATE_MAX) : undefined,
        postalCode: validatePostalCode(address.postalCode),
        country: validateCountryCode(address.country || 'ES')
    }
}


// ============================================
// EXPORT OBJECT - Legacy compatible
// ============================================
const validate = {
    // IDs
    userId: validateUserId,
    id: validateId,
    uuid: validateUuid,
    positiveId: validatePositiveId,

    // User fields
    name: validateName,
    username: validateUsername,
    email: validateEmail,
    password: validatePassword,
    passwordsMatch: validatePasswordsMatch,
    usernameOrEmail: validateUsernameOrEmail,
    phone: validatePhone,
    bio: validateBio,

    // Content
    description: validateDescription,
    text: validateText,
    notes: validateNotes,
    image: validateImage,
    url: validateUrl,
    httpsRecipeImportImageUrl: validateHttpsRecipeImportImageUrl,

    // Business
    packName: validatePackName,
    currency: validateCurrency,
    paymentMethod: validatePaymentMethod,
    payedAmount: validatePayedAmount,
    promoAmount: validatePromoAmount,

    // Numbers
    integerNum: validateInteger,
    number: validateNumber,
    quantity: validateQuantity,

    // Dates/Time
    date: validateDate,
    timeFormat: validateTimeFormat,
    expiring: validateExpiringTime,

    // Enums
    status: validateStatus,
    taskStatus: validateTaskStatus,
    taskPriority: validateTaskPriority,
    units: validateUnit,
    method: validateMethod,

    // Documents
    dni: validateDni,

    // Arrays
    attendees: validateAttendees,
    array: validateArray,

    // Preferences
    diet: validateDiet,
    dietsArray: validateDietsArray,

    // Allergens (catalog)
    allergenName: validateAllergenName,

    // User profile (demographics + intent)
    ageRange: validateAgeRange,
    gender: validateGender,
    occupationContext: validateOccupationContext,
    primaryGoal: validatePrimaryGoal,
    cookingFrequency: validateCookingFrequency,
    cookingTimePreference: validateCookingTimePreference,

    // Recipe feedback
    recipeFeedbackAction: validateRecipeFeedbackAction,
    recipeFeedbackReason: validateRecipeFeedbackReason,
    recipeFeedbackComment: validateRecipeFeedbackComment,

    // Address
    fullName: validateFullName,
    addressLine: validateAddressLine,
    city: validateCity,
    postalCode: validatePostalCode,
    countryCode: validateCountryCode,
    addressFormat: validateAddressFormat,

    // Generic
    generic: validateGenericField,
    callback: validateCallback,
    string: validateString
}

export default validate
export { LIMITS, REGEX, VALID_VALUES }
